import type { AgentRunsListItem, MatchSide } from '~/entities/document-agent'
import { getRunTotalCost, REVIEWED_STATUSES } from '~/entities/document-agent'
import { SCORED_VERDICTS } from '../config'

export type Rate = { correct: number; n: number }

export type SidePerformance = { all: Rate; ruleAbstained: Rate }

export type RunsPerformance = {
  documents: number
  category: Rate
  matchStatuses: Record<string, number>
  sides: Record<MatchSide, SidePerformance>
  meanCost: { total: number | null; classification: number | null; matching: number | null }
}

function mean(values: Array<number | null | undefined>): number | null {
  const numbers = values.filter((value): value is number => typeof value === 'number')
  return numbers.length ? numbers.reduce((sum, value) => sum + value, 0) / numbers.length : null
}

function add(rate: Rate, isCorrect: boolean) {
  rate.n += 1
  if (isCorrect) rate.correct += 1
}

/** Over each loaded document's newest run, the one the table shows. */
export function getRunsPerformance(items: AgentRunsListItem[]): RunsPerformance {
  const performance: RunsPerformance = {
    documents: items.length,
    category: { correct: 0, n: 0 },
    matchStatuses: {},
    sides: {
      claim: { all: { correct: 0, n: 0 }, ruleAbstained: { correct: 0, n: 0 } },
      pa: { all: { correct: 0, n: 0 }, ruleAbstained: { correct: 0, n: 0 } },
    },
    meanCost: { total: null, classification: null, matching: null },
  }
  const runs = items.flatMap((item) => (item.runs[0] ? [{ item, run: item.runs[0] }] : []))

  for (const { item, run } of runs) {
    if (
      run.agent_category &&
      item.final_category &&
      REVIEWED_STATUSES.has(item.review_status ?? '')
    ) {
      add(performance.category, run.agent_category === item.final_category)
    }
    if (run.match_status) {
      performance.matchStatuses[run.match_status] =
        (performance.matchStatuses[run.match_status] ?? 0) + 1
    }
    const side = item.final_side
    const summary = side ? run[side] : null
    if (side && summary?.verdict && SCORED_VERDICTS.has(summary.verdict)) {
      const isCorrect = summary.verdict === 'correct'
      add(performance.sides[side].all, isCorrect)
      if (summary.today_rule_fired === false) add(performance.sides[side].ruleAbstained, isCorrect)
    }
  }

  performance.meanCost = {
    total: mean(runs.map(({ run }) => getRunTotalCost(run))),
    classification: mean(runs.map(({ run }) => run.cost_usd)),
    matching: mean(runs.map(({ run }) => run.match_cost_usd)),
  }
  return performance
}
